import React, { useState } from 'react';
import { Box, Divider, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { SlidingAnimationPageBase } from '../../common/components/SlidingAnimationPageBase';
import { LoadMoreNotificationsCard } from './LoadMoreNotificationsCard';
import { NotificationGroupingSelectMenu } from './NotificationGroupingSelectMenu';
import {
  getAllVisibleNotificationIds,
  useLoadMoreNotifications,
  useNotificationSearchMatcher,
} from './notifications-helper';
import { NotificationGrouping } from './notificationsApi';
import {
  NotificationsBulkActionBar,
  NotificationsEditButton,
  NotificationsSelectAllCheckbox,
} from './NotificationsBulkActions';
import { NotificationsListChronological } from './NotificationsListChronological';
import { NotificationsListPriorityStatus } from './NotificationsListPriorityStatus';
import { NotificationsListReadStatus } from './NotificationsListReadStatus';
import { NotificationsSearchInput } from './NotificationsSearchInput';
import { useNotificationsBulkEdit } from './use-notifications-bulk-edit';

type NotificationsArchiveState = {
  groupBy: NotificationGrouping;
};

export function NotificationsArchive() {
  const { t } = useTranslation();
  const [state, setState] = useState<NotificationsArchiveState>({
    groupBy: getInitialGrouping(),
  });
  const { groupBy } = state;
  const [searchQuery, setSearchQuery] = useState('');

  // All notifications load into the one page — the query limit grows when "Load more" is clicked.
  // This query is deduplicated against the ones inside each list view component (same cache entry).
  const { data, limit, hasMore, isFetching, loadMore } = useLoadMoreNotifications(groupBy, true, searchQuery);
  const matchesSearch = useNotificationSearchMatcher(searchQuery);
  const allVisibleNotifications = getAllVisibleNotificationIds(data, groupBy, matchesSearch);

  const bulkEdit = useNotificationsBulkEdit(allVisibleNotifications);

  function getInitialGrouping() {
    const fromLocalStorage = localStorage.getItem('notificationsArchiveGroupBy');
    if (fromLocalStorage) {
      return fromLocalStorage as NotificationGrouping;
    }

    return 'CHRONOLOGICAL';
  }

  function handleUpdateGrouping(newGrouping: NotificationGrouping) {
    localStorage.setItem('notificationsArchiveGroupBy', newGrouping);

    setState({
      groupBy: newGrouping,
    });
  }

  const commonListProps = {
    groupBy,
    limit,
    searchQuery,
    isArchived: true,
    isEditMode: bulkEdit.showAllCheckboxes,
    selectedIds: bulkEdit.selectedIds,
    onToggleSelect: bulkEdit.handleToggleSelectWithAutoExit,
    onSwipeSelect: bulkEdit.handleSwipeSelect,
  };

  const selectedGroupingToComponent: Record<NotificationGrouping, React.JSX.Element> = {
    CHRONOLOGICAL: <NotificationsListChronological {...commonListProps} />,
    UNREAD: <NotificationsListReadStatus {...commonListProps} />,
    PRIORITY: <NotificationsListPriorityStatus {...commonListProps} />,
  };

  return (
    <SlidingAnimationPageBase backURL={`../list?direction=back`} title={t('Notifications.archive')}>
      <Box px={3}>
        <Flex align="center" justify="space-between">
          <NotificationGroupingSelectMenu onSelectGrouping={handleUpdateGrouping} selectedGrouping={groupBy} />
          <NotificationsSearchInput onSearch={setSearchQuery} />
          <NotificationsEditButton bulkEdit={bulkEdit} />
        </Flex>

        <Divider mt={2} mb={4} />

        <NotificationsSelectAllCheckbox bulkEdit={bulkEdit} />

        {selectedGroupingToComponent[groupBy]}

        {hasMore && <LoadMoreNotificationsCard onClick={loadMore} isLoading={isFetching} />}
      </Box>

      {/* Archived notifications can only be deleted or marked read/unread, not archived again */}
      <NotificationsBulkActionBar bulkEdit={bulkEdit} includeArchiveAction={false} />
    </SlidingAnimationPageBase>
  );
}
